import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Pricing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const plans = t("pricing.plans", { returnObjects: true }) as {
    name: string; price: string; priceNote?: string; description: string; features: string[]; cta: string;
  }[];

  return (
    <section id="pricing" className="py-28 bg-background border-t border-border/40">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl md:text-5xl font-extralight text-foreground tracking-tight">
            {t("pricing.title")}
          </h2>
          <p className="mt-6 text-muted-foreground max-w-lg mx-auto text-base font-light">
            {t("pricing.subtitle")}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((plan, i) => {
            const featured = i === 1;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className={`rounded-2xl p-9 ${
                  featured
                    ? "bg-foreground text-background"
                    : "bg-background border border-border/60"
                }`}
              >
                {featured && (
                  <span className="inline-block text-[10px] font-light uppercase tracking-[0.25em] text-background/50 mb-4">
                    {t("pricing.mostPopular")}
                  </span>
                )}
                <h3 className={`text-lg font-light ${featured ? "" : "text-foreground"}`}>
                  {plan.name}
                </h3>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-4xl font-extralight">{plan.price}</span>
                  {plan.priceNote && (
                    <span className={`text-xs font-light ${featured ? "opacity-50" : "text-muted-foreground"}`}>
                      {plan.priceNote}
                    </span>
                  )}
                </div>
                <p className={`mt-3 text-sm font-light ${featured ? "opacity-60" : "text-muted-foreground"}`}>
                  {plan.description}
                </p>
                <ul className="mt-8 space-y-3">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-3 text-sm font-light">
                      <Check size={14} strokeWidth={1.5} className={`mt-0.5 flex-shrink-0 ${featured ? "opacity-50" : "text-muted-foreground"}`} />
                      <span className={featured ? "opacity-80" : "text-foreground"}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-10 w-full rounded-full font-light tracking-wide shadow-none ${
                    featured
                      ? "bg-background text-foreground hover:bg-background/90"
                      : "bg-foreground text-background hover:bg-foreground/85"
                  }`}
                  size="lg"
                  onClick={() => navigate("/create")}
                >
                  {plan.cta}
                </Button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
